import VirtualScroll from 'virtual-scroll'
import values from 'src/values'
import raf from 'src/utils/raf'
import resize from 'src/utils/resize'
import { TweenMax, Linear } from 'gsap'

const smoothOptionsDefaultsDesktop = {
  touchMultiplier: 1.3,
  mouseMultiplier: 0.5,
  firefoxMultiplier: 56
}

const smoothOptionsDefaultsMobile = {
  touchMultiplier: 4,
  mouseMultiplier: 1.6,
  firefoxMultiplier: 56
}

const scrollDefaults = {
  targetY: 0,
  currentY: 0,
  targetX: 0,
  currentX: 0,
  delta: 0,
  inertiaDelta: 0
}

class Scroller {
  constructor(options) {
    this.inertia = values.sniffer.isDesktop ? 0.15 : 0.1
    this.resetScroll()

    this.key = 'scroller'

    const smoothOptionsDefaults = values.sniffer.isDesktop
      ? smoothOptionsDefaultsDesktop
      : smoothOptionsDefaultsMobile

    this.options = Object.assign({}, options)
    this.options.smoothOptions = Object.assign(
      {},
      smoothOptionsDefaults,
      options.smoothOptions
    )

    if (options.debug) {
      window.Scroller = this
    }

    this.virtualScroll = new VirtualScroll({
      touchMultiplier: this.options.smoothOptions.touchMultiplier,
      mouseMultiplier: this.options.smoothOptions.mouseMultiplier,
      firefoxMultiplier: this.options.smoothOptions.firefoxMultiplier
    })
  }

  /**
   * Listen events based on scroll type
   */
  listen() {
    this.body = document.getElementsByTagName('html')[0]
    this.app = document.getElementsByClassName('app')[0]

    resize.add(this.onResize, this.key)
    this.onResize()

    raf.add(this.raf)

    window.addEventListener('touchmove', e => {
      !this.activate && e.preventDefault()
    })
    this.virtualScroll.on(this.onScroll)
  }

  /**
   * Unlisten all events
   */
  unlisten() {
    console.log('Scroller', '[Smooth] Stop listening...')

    raf.remove(this.raf)
    resize.remove(this.resize, this.key)
    this.virtualScroll.off(this.onScroll)
  }

  /**
   * Start Scroller
   */
  start() {
    this.activate()
    this.listen()
  }

  /**
   * Stop Scroller
   */
  stop() {
    this.deactivate()
    this.unlisten()
  }

  /**
   * Enable targetY global value update
   */
  activate() {
    this.active = true
  }

  /**
   * Disable targetY global value update
   */
  desactivate() {
    this.active = false
  }

  /**
   * Update targetY in global values
   */
  onScroll = e => {
    if (!this.active) return

    this.inertiaDelta += -e.deltaY

    values.scroll.isScrolling = true

    if (this.horizontalDirection) {
      if (!this.options.smoothOptions.overflow) {
        this.targetX += -e.deltaY
      } else {
        this.targetX = this.body.scrollLeft + values.viewport.width
      }

      if (this.options.smoothOptions.infinite) {
        if (this.targetX > values.scroll.height) {
          this.targetX = values.scroll.width - this.currentX - e.deltaY
          this.currentX = 0
        } else if (this.targetY < 0) {
          this.targetX = values.scroll.width - this.currentX - e.deltaY
          this.currentX = values.scroll.width
        }
      } else {
        this.targetX = Math.max(0, Math.min(values.scroll.width, this.targetX))
      }

      this.targetX = Math.floor(this.targetX)
    } else {
      if (!this.options.smoothOptions.overflow) {
        this.targetY += -e.deltaY
      } else {
        this.targetY = this.body.scrollTop + values.viewport.height
      }

      if (this.options.smoothOptions.infinite) {
        if (this.targetY > values.scroll.height) {
          this.targetY = values.scroll.height - this.currentY - e.deltaY
          this.currentY = 0
        } else if (this.targetY < 0) {
          this.targetY = values.scroll.height - this.currentY - e.deltaY
          this.currentY = values.scroll.height
        }
      } else {
        this.targetY = Math.max(0, Math.min(values.scroll.height, this.targetY))
      }

      this.targetY = Math.floor(this.targetY)
    }
  }

  /**
   * Update scrolling key and element
   */
  update({ key, el, params, horizontal }) {
    this.el = el
    this.key = key
    this.horizontalDirection = horizontal
    this.options.smoothOptions = Object.assign({}, params)

    values.scroll.currentY = 0
    values.scroll.targetY = 0
    values.scroll.targetX = 0
    values.scroll.targetX = 0

    this.updateScrollHeight()
    this.resetScroll()
  }

  /**
   * Update scrolling key and element
   */
  updateScrollHeight() {
    if (this.horizontalDirection) {
      if (this.el && !this.options.smoothOptions.viewportHeightMultiple) {
        values.scroll.width =
          this.el.getBoundingClientRect().width - values.viewport.width
      } else if (this.options.smoothOptions.viewportWidthMultiple) {
        values.scroll.width =
          this.options.smoothOptions.viewportWidthMultiple *
          values.viewport.width
      }
    } else {
      if (this.el && !this.options.smoothOptions.viewportHeightMultiple) {
        values.scroll.height =
          this.el.getBoundingClientRect().height - values.viewport.height
      } else if (this.options.smoothOptions.viewportHeightMultiple) {
        values.scroll.height =
          this.options.smoothOptions.viewportHeightMultiple *
          values.viewport.height
      }
    }

    if (!this.options.smoothOptions.overflow && !values.isMobile) {
      TweenMax.set(this.body, {
        position: 'absolute',
        overflow: 'hidden'
      })
    } else {
      TweenMax.set(this.body, {
        position: 'relative',
        overflow: 'auto'
      })
    }
  }

  /**
   * Reset internal scroll values to default
   */
  resetScroll() {
    Object.assign(this, scrollDefaults)
  }

  /**
   * Scroll to specific position
   * @param {number} y position
   * @param {number} scroll duration
   * @param {boolean} modify duration based on distance to scroll
   */
  scrollTo(target, t = 0.8, distanceRelative = false) {
    return new Promise(resolve => {
      if (this.magnets) clearTimeout(this.magnets)
      this.scrollToActive = true

      const timeFromDistance = Math.abs(t * (this.currentY - target) * 0.0008)

      if (this.horizontalDirection) {
        target = target > values.scroll.width ? values.scroll.width : target

        this.scrollToTween = TweenMax.to(
          this,
          distanceRelative ? timeFromDistance : t,
          {
            currentX: target,
            targetX: target,
            ease: Linear.easeNone,
            onUpdate: () => {
              this.currentX = Math.floor(this.currentX)
              this.targetX = Math.floor(this.targetX)
            },
            onComplete: () => {
              resolve()
              this.scrollToActive = false
            }
          }
        )
      } else {
        target = target > values.scroll.height ? values.scroll.height : target

        this.scrollToTween = TweenMax.to(
          this,
          distanceRelative ? timeFromDistance : t,
          {
            currentY: target,
            targetY: target,
            ease: Linear.easeNone,
            onUpdate: () => {
              this.currentY = Math.floor(this.currentY)
              this.targetY = Math.floor(this.targetY)
            },
            onComplete: () => {
              resolve()
              this.scrollToActive = false
            }
          }
        )
      }
    })
  }

  onResize = () => {
    this.updateScrollHeight()
  }

  raf = () => {
    values.scroll.inertiaDelta = Math.trunc(this.inertiaDelta)

    if (this.horizontalDirection) {
      this.delta = (this.targetX - this.currentX) * 600 / values.viewport.width
      values.scroll.delta = Math.trunc(this.delta)

      this.currentX += (this.targetX - this.currentX) * this.inertia

      this.currentX =
        this.currentX > values.scroll.width
          ? values.scroll.width
          : this.currentX

      values.scroll.direction =
        values.scroll.targetX - values.scroll.currentX < 0 ? 1 : -1
      values.scroll.direction =
        values.scroll.targetX == values.scroll.currentX
          ? 0
          : values.scroll.direction

      values.scroll.currentX = this.currentX
      values.scroll.targetX = this.targetX

      if (values.scroll.targetX > values.scroll.width) {
        values.scroll.targetX = values.scroll.width
        this.scrollTo(values.scroll.targetX, 0)
        resize.process()
      }
    } else {
      this.delta = (this.targetY - this.currentY) * 600 / values.viewport.height
      values.scroll.delta = Math.trunc(this.delta)

      this.currentY += (this.targetY - this.currentY) * this.inertia

      this.currentY =
        this.currentY > values.scroll.height
          ? values.scroll.height
          : this.currentY

      values.scroll.direction =
        values.scroll.targetY - values.scroll.currentY < 0 ? 1 : -1
      values.scroll.direction =
        values.scroll.targetY == values.scroll.currentY
          ? 0
          : values.scroll.direction

      values.scroll.currentY = this.currentY
      values.scroll.targetY = this.targetY

      if (values.scroll.targetY > values.scroll.height) {
        values.scroll.targetY = values.scroll.height
        this.scrollTo(values.scroll.targetY, 0)
        resize.process()
      }
    }

    const roundCurrentY = Math.ceil(values.scroll.currentY)

    values.scroll.isntScrolling =
      roundCurrentY == values.scroll.targetY ||
      roundCurrentY + 1 == values.scroll.targetY ||
      roundCurrentY - 1 == values.scroll.targetY
  }
}

export default new Scroller({
  smoothOptions: {
    infinite: false
    // Add custom options here :)
  }
})



// WEBPACK FOOTER //
// ./src/utils/scroller.js